/**
 * Feedback Service — ระบบบันทึกแจ้งปัญหา/Feedback (`/api/v1/feedback`)
 *
 * ชนิดข้อมูลทุกตัว mirror `docs/FEEDBACK_SYSTEM_DESIGN.md` — แก้ที่นี่ที่เดียวเมื่อ contract เปลี่ยน
 * สิทธิ์การกระทำใช้ `detail.can.*` จาก API เท่านั้น (ห้าม FE คิดสิทธิ์ซ้ำเอง)
 */
import ApiWithLogging from '../utils/apiWithLogging';
import type {
  FeedbackCategory,
  FeedbackEventType,
  FeedbackPriority,
  FeedbackStatus,
} from '../utils/feedbackLabels';

export const FEEDBACK_PERMS = {
  view: 'feedback.view',
  manage: 'feedback.manage',
  export: 'feedback.export',
} as const;

const BASE = '/feedback';
const LOG = { service: 'feedback', entityType: 'feedback' } as const;

export interface FeedbackUser {
  id: number;
  full_name: string | null;
  username: string;
}

export interface FeedbackItem {
  id: number;
  report_number: string;
  category: FeedbackCategory;
  status: FeedbackStatus;
  priority: FeedbackPriority | null;
  message_preview: string;
  page_url: string | null;
  reporter: FeedbackUser;
  assignee: FeedbackUser | null;
  attachment_count: number;
  comment_count: number;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface FeedbackListResponse {
  items: FeedbackItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface FeedbackSummary {
  total: number;
  open: number;
  by_status: Partial<Record<FeedbackStatus, number>>;
  by_category: Partial<Record<FeedbackCategory, number>>;
}

export interface FeedbackAttachment {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: FeedbackUser | null;
  created_at: string;
  /** path ของ GET attachment (ต้องใช้ token — ดึงเป็น blob ผ่าน getAttachmentBlob) */
  url: string;
  /** server ตัดสินแล้วว่าผู้ใช้ปัจจุบันลบรูปนี้ได้ไหม (ผู้อัปโหลดเอง หรือ feedback.manage) */
  can_delete?: boolean;
}

export interface FeedbackEvent {
  id: number;
  event_type: FeedbackEventType | string;
  actor: FeedbackUser | null;
  /** EDITED: 'message' | 'category' · อื่น ๆ: status/priority/assignee */
  field?: string | null;
  from_value: string | null;
  to_value: string | null;
  note: string | null;
  created_at: string;
}

export interface FeedbackCan {
  edit: boolean;
  change_status: boolean;
  add_attachment: boolean;
  manage: boolean;
  comment: boolean;
}

export interface FeedbackDetail extends FeedbackItem {
  message: string;
  user_agent: string | null;
  resolution_note: string | null;
  fixed_ref: string | null;
  duplicate_of: { id: number; report_number: string } | null;
  email_sent: boolean;
  attachments: FeedbackAttachment[];
  events: FeedbackEvent[];
  can: FeedbackCan;
}

export interface FeedbackSubmitResponse {
  ok: boolean;
  id: number;
  report_number: string;
  email_sent: boolean;
  /** additive (E2E B8) — server เก่าไม่ส่ง */
  attachments_saved?: number;
  attachments_failed?: number;
}

export interface FeedbackScreenshot {
  base64: string;
  type: string;
}

/** ตัวกรองรายการ/summary/export — ชุดเดียวกัน (export-filter-parity) */
export interface FeedbackFilters {
  status?: string; // คั่นด้วย ,
  open_only?: boolean;
  category?: string;
  priority?: string;
  reporter_id?: number | string;
  assignee_id?: number | string;
  q?: string;
  date_from?: string;
  date_to?: string;
  sort?: string;
}

export interface FeedbackListParams extends FeedbackFilters {
  page?: number;
  page_size?: number;
}

export interface FeedbackStatusPayload {
  status: FeedbackStatus;
  note?: string;
  fixed_ref?: string;
  duplicate_of_id?: number;
  /** สถานะที่เห็นบนจอ — server ตอบ 409 ถ้าถูกเปลี่ยนไปแล้ว (B10) */
  expected_status?: FeedbackStatus;
}

export interface FeedbackPatchPayload {
  message?: string;
  category?: FeedbackCategory;
  priority?: FeedbackPriority | null;
  assignee_id?: number | null;
  /** ข้อความตอนเปิดฟอร์มแก้ — server ตอบ 409 ถ้าถูกแก้จากที่อื่นไปแล้ว (B10) */
  expected_message?: string;
}

/** ตัด key ที่ว่าง/false ออก — ใช้ร่วมกันทั้ง list / summary / export */
export function cleanFeedbackParams<T extends object>(params: T): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || v === false) return;
    out[k] = v as string | number | boolean;
  });
  return out;
}

const feedbackService = {
  submit: (payload: {
    category: FeedbackCategory;
    message: string;
    page_url: string;
    screenshots: FeedbackScreenshot[];
  }) =>
    ApiWithLogging.post<FeedbackSubmitResponse>(`${BASE}/submit`, payload, {}, { ...LOG, action: 'submit' }),

  getMine: (params: { status?: string; page?: number; page_size?: number } = {}) =>
    ApiWithLogging.get<FeedbackListResponse>(`${BASE}/mine`, { params: cleanFeedbackParams(params) }),

  list: (params: FeedbackListParams = {}) =>
    ApiWithLogging.get<FeedbackListResponse>(BASE, { params: cleanFeedbackParams(params) }),

  /** summary รับ filter ชุดเดียวกับ list (backend ละ status เอง) */
  summary: (params: FeedbackFilters = {}) =>
    ApiWithLogging.get<FeedbackSummary>(`${BASE}/summary`, { params: cleanFeedbackParams(params) }),

  exportExcel: (params: FeedbackFilters = {}) =>
    ApiWithLogging.get<Blob>(`${BASE}/export`, { params: cleanFeedbackParams(params), responseType: 'blob' }),

  assignees: async (): Promise<FeedbackUser[]> => {
    const res = await ApiWithLogging.get<FeedbackUser[] | { items: FeedbackUser[] }>(`${BASE}/assignees`);
    return Array.isArray(res) ? res : res?.items ?? [];
  },

  /** ผู้ที่เคยแจ้งเรื่อง (feedback.view) — ตัวเลือกช่องกรอง "ผู้แจ้ง" */
  reporters: async (): Promise<FeedbackUser[]> => {
    const res = await ApiWithLogging.get<FeedbackUser[] | { items: FeedbackUser[] }>(`${BASE}/reporters`);
    return Array.isArray(res) ? res : res?.items ?? [];
  },

  get: (id: number) => ApiWithLogging.get<FeedbackDetail>(`${BASE}/${id}`),

  patch: (id: number, payload: FeedbackPatchPayload) =>
    ApiWithLogging.patch<FeedbackDetail>(`${BASE}/${id}`, payload, {}, { ...LOG, action: 'update' }),

  changeStatus: (id: number, payload: FeedbackStatusPayload) =>
    ApiWithLogging.post<FeedbackDetail>(`${BASE}/${id}/status`, payload, {}, { ...LOG, action: 'status' }),

  comment: (id: number, message: string) =>
    ApiWithLogging.post<FeedbackDetail>(`${BASE}/${id}/comments`, { message }, {}, { ...LOG, action: 'comment' }),

  addAttachments: (id: number, screenshots: FeedbackScreenshot[]) =>
    ApiWithLogging.post<FeedbackDetail>(
      `${BASE}/${id}/attachments`,
      { screenshots },
      {},
      { ...LOG, action: 'add_attachment' }
    ),

  deleteAttachment: (id: number, attId: number) =>
    ApiWithLogging.delete(`${BASE}/${id}/attachments/${attId}`, {}, { ...LOG, action: 'delete_attachment' }),

  getAttachmentBlob: (id: number, attId: number) =>
    ApiWithLogging.get<Blob>(`${BASE}/${id}/attachments/${attId}`, { responseType: 'blob' }),

  deleteReport: (id: number) => ApiWithLogging.delete(`${BASE}/${id}`, {}, { ...LOG, action: 'delete' }),
};

export default feedbackService;
